import { useState } from 'react';
import { useWageProStore } from './wagepro_store';
import { usePayRuns } from './use_pay_runs';
import type { PayRunsReadState } from './use_pay_runs';
import { hasPostedPayRun } from './wagepro_store_settings';
import type {
  OrganisationActionsController,
  OrganisationSaveOutcome,
} from './wagepro_store';
import {
  getTrpcClient,
  isForbiddenTrpcError,
  isPayrollAddonRequiredError,
} from '../lib/trpc.js';
import type { OAuthEntityContext } from '../lib/auth/oauth_entity_context.js';
import type { SessionFactsResult } from './use_session_facts.js';
import { MOCK_OPERATIONS_SETTINGS } from './mock_data';
import type { OperationsSettings, OrganisationSettings } from './types';
import type { OrganisationUpdateFields } from './wagepro_store_settings.js';

export interface OrganisationSaveResult {
  ok: boolean;
  /** `true` only when the save failed specifically because the backend's base-currency lock rejected it -- see `wagepro_store.tsx`'s `update_organisation` doc comment. */
  currency_locked: boolean;
}

export interface UseSettingsResult {
  organisation: OrganisationSettings;
  update_organisation: (
    org: OrganisationSettings,
  ) => Promise<OrganisationSaveResult>;
  /** `true` while a real Organisation-settings save is in flight (C6, #21937) -- always `false` in mock mode, since that path resolves synchronously. */
  is_saving_organisation: boolean;
  /**
   * `true` when any already-resolved REAL pay run has posted (`'approved'`
   * or `'paid'`) -- a real journal certainly exists for this entity, so the
   * base-currency field should be disabled pre-emptively rather than
   * waiting for a save attempt to fail. See {@link resolveHasPostedPayRun}
   * for how this is derived (adversarial review, PR #22375 finding 6).
   */
  has_posted_pay_run: boolean;
  /**
   * `operations` below has NO backend concept at all (no `entities` column,
   * no procedure, nothing to read or write) -- unlike `organisation` (real,
   * C6/C11) and `payroll` (real as of #22729/#22730, see
   * `use_payroll_settings.ts`'s `usePayrollSettings()` -- the Settings
   * screen's Payroll tab reads that hook directly rather than through this
   * one), `operations` remains permanently mock. Out of scope for #22333;
   * do not invent a backend for it here. See `mock_data.ts`'s doc comment on
   * `MOCK_OPERATIONS_SETTINGS` for the same note at the fixture's
   * definition.
   */
  operations: OperationsSettings;
  is_loading: boolean;
}

/**
 * Pure derivation, exported separately so it's unit-testable without
 * rendering `usePayRuns()` (this app has no jsdom setup -- same
 * "pure decision function + thin hook" split `resolvePayRunsReadState`/
 * `resolveDefaultPayrollAccounts` use in `use_pay_runs.ts`).
 *
 * `true` only when the real, self-fetching `usePayRuns()` read state (C3/C7,
 * #22327/#22329) is `ready` AND at least one of its resolved pay runs has
 * posted (`'approved'`/`'paid'`). The store's OWN `has_posted_pay_run` field
 * -- the boot-time-seed fallback this used to OR in defensively -- is gone
 * as of C12 (#22334): `wagepro_store.tsx`'s `pay_run_records` prop was never
 * fed by any real call site (nothing ever passed it -- see that module's
 * doc comment), so the store's own flag was permanently `false` and OR-ing
 * it in was a no-op. Every other status (`loading`/`entitlement_required`/
 * `error`/`no_entity`) resolves `false` -- there is no confirmed real pay-run
 * history to disable the field pre-emptively for.
 */
export function resolveHasPostedPayRun(
  payRunsState: PayRunsReadState,
): boolean {
  return (
    payRunsState.status === 'ready' && hasPostedPayRun(payRunsState.pay_runs)
  );
}

/** Company settings: organisation, roles and operations. Payroll config lives in `use_payroll_settings.ts`'s `usePayrollSettings()` instead (#22730) -- `settings_screen.tsx` calls that hook directly for its Payroll tab. */
export function useSettings(): UseSettingsResult {
  const { organisation, update_organisation: store_update_organisation } =
    useWageProStore();
  const { state: pay_runs_state } = usePayRuns();
  const [is_saving_organisation, set_is_saving_organisation] = useState(false);

  async function update_organisation(
    org: OrganisationSettings,
  ): Promise<OrganisationSaveResult> {
    set_is_saving_organisation(true);
    try {
      const outcome = await store_update_organisation(org);
      return {
        ok: outcome.ok,
        currency_locked: outcome.reason === 'currency_locked',
      };
    } finally {
      set_is_saving_organisation(false);
    }
  }

  return {
    organisation,
    update_organisation,
    is_saving_organisation,
    has_posted_pay_run: resolveHasPostedPayRun(pay_runs_state),
    operations: MOCK_OPERATIONS_SETTINGS,
    is_loading: false,
  };
}

// ── Real production save path (C11, #22333) ─────────────────────────────────
//
// Everything below replaces `lib/auth/dev_settings.ts` (deleted this pass):
// the READ side no longer needs a resolver at all -- `use_session_facts.ts`
// already fetches `entity.clientGet` once (C1) and now also exposes the
// organisation fields from that SAME response (see that module's docblock),
// which `main.tsx` threads into `WageProStoreProvider`'s `organisation_record`
// prop exactly like the dev-bypass version did. Only the WRITE side
// (`entity.update`) needed new code: the real bearer client
// (`getTrpcClient()`, never the dev-bypass cookie client) plus the same
// currency-lock / FORBIDDEN-cause classification `dev_settings.ts` used to do.

/**
 * The exact `TRPCError` message `entities.update` throws when `currency_code`
 * is changed on an entity that already has a posted journal (Phase F,
 * `apps/api/src/trpc/routers/entities.ts`'s `update` procedure) -- relocated
 * verbatim from the deleted `lib/auth/dev_settings.ts` (nothing else in this
 * app referenced that constant). Matching the exact message (not just
 * `BAD_REQUEST` generically, which the same procedure also throws for an
 * invalid entity_type/country pairing) is deliberate -- see
 * `wagepro_store_settings.ts`'s module docblock for the full rationale.
 */
const CURRENCY_LOCKED_MESSAGE =
  'Cannot change base currency after transactions exist';

/**
 * Duck-typed `.message` check (not `err instanceof Error`) — the same
 * shape-checking stance `lib/trpc.ts`'s `isForbiddenTrpcError` takes, so a
 * test/mock `{ message: '...' }` fixture that doesn't happen to extend
 * `Error` still classifies correctly (adversarial-review finding, C11
 * #22333, second round). Deliberately NOT gated on `isForbiddenTrpcError`
 * first, unlike `isPayrollAddonRequiredError` — the currency lock is thrown
 * as `TRPCError({ code: 'BAD_REQUEST', ... })` server-side (see
 * `apps/api/src/trpc/routers/entities.ts`'s `update` procedure), never
 * `FORBIDDEN`, so requiring the FORBIDDEN shape here would make this
 * classifier permanently unreachable.
 */
function isCurrencyLockedError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    (err as { message?: unknown }).message === CURRENCY_LOCKED_MESSAGE
  );
}

/**
 * Calls the REAL `entity.update` mutation via the bearer client
 * (`getTrpcClient()`) with an already-built delta (`buildEntityUpdateInput`,
 * `wagepro_store_settings.ts`) plus the resolved `{firm_id, entity_id}` --
 * this function never decides what changed or where the ids come from, only
 * sends what it's given, mirroring `dev_settings.ts`'s deleted
 * `saveOrganisationSettings` contract exactly. Never throws: every failure
 * mode resolves `{ ok: false, reason }`.
 *
 * FORBIDDEN-cause classification (critical lesson carried over from C3's
 * adversarial review, restated in `lib/trpc.ts`'s `isPayrollAddonRequiredError`
 * docblock): a bare `isForbiddenTrpcError` match is NOT enough to decide this
 * was an entitlement problem -- `isPayrollAddonRequiredError` requires the
 * EXACT missing-add-on message on top of the FORBIDDEN shape. Every other
 * FORBIDDEN (wrong OAuth scope, an IDOR capability mismatch, the
 * firm-membership/`edit_settings` RBAC gate, an OAuth-only rejected field, a
 * null-audience rejection, ...) resolves to the same generic `'error'` a
 * network failure would, never a fabricated success.
 */
export interface RealEntityRow {
  name: string;
  financial_year_end: number;
  currency_code: string;
}

/**
 * Dependency-injection seam so `saveRealOrganisationSettings` is unit
 * -testable without `mock.module()`-ing `lib/trpc.ts`'s `getTrpcClient()` --
 * mirrors the deleted `dev_settings.ts`'s `DevSettingsDeps` pattern (per
 * CLAUDE.md's preference for DI over `mock.module()` for new tests).
 */
export interface RealSettingsDeps {
  updateEntity: (
    input: { firm_id: string; entity_id: string } & OrganisationUpdateFields,
  ) => Promise<{ entity?: RealEntityRow }>;
}

function defaultDeps(): RealSettingsDeps {
  return {
    updateEntity: (input) => getTrpcClient().entity.update.mutate(input),
  };
}

export async function saveRealOrganisationSettings(
  input: OrganisationUpdateFields,
  context: { firm_id: string; entity_id: string },
  deps: RealSettingsDeps = defaultDeps(),
): Promise<OrganisationSaveOutcome> {
  try {
    const { entity } = await deps.updateEntity({
      firm_id: context.firm_id,
      entity_id: context.entity_id,
      ...input,
    });
    if (!entity) {
      // Should never happen in practice (`entity.update`'s real return type
      // marks `entity` optional only because its own `results.at(0)` has no
      // null guard server-side) -- defensive, not a non-null assertion.
      console.error(
        '[wagepro-web] save organisation settings: entity.update returned no entity row',
      );
      return { ok: false, reason: 'error' };
    }
    return {
      ok: true,
      record: {
        name: entity.name,
        financial_year_end: entity.financial_year_end,
        currency_code: entity.currency_code,
      },
    };
  } catch (err) {
    if (isCurrencyLockedError(err)) {
      return { ok: false, reason: 'currency_locked' };
    }
    if (isPayrollAddonRequiredError(err)) {
      // Not reachable from `entity.update` today -- neither it nor
      // `entity.clientGet` is gated behind `requirePayrollAddon` (see
      // `lib/trpc.ts`'s `isPayrollAddonRequiredError` docblock). Checked
      // anyway for defensive completeness and because this is the shared
      // classifier every other C-child's add-on-gated procedure needs.
      return { ok: false, reason: 'entitlement_required' };
    }
    if (isForbiddenTrpcError(err)) {
      console.error(
        '[wagepro-web] save organisation settings: entity.update was forbidden -- this session is ' +
          'not a firm member of this entity, or the OAuth grant lacks payroll:employer:write.',
      );
      return { ok: false, reason: 'error' };
    }
    console.error(
      '[wagepro-web] save organisation settings: entity.update failed',
      err,
    );
    return { ok: false, reason: 'error' };
  }
}

/**
 * The `hasRealOrganisation` guard (C11, #22333): only wires a real
 * `OrganisationActionsController` when BOTH (a) a real entity context is
 * bound to this OAuth grant, AND (b) the real Organisation-settings READ
 * genuinely succeeded (`sessionFactsStatus === 'ready'`, which per
 * `resolveSessionFactsState`'s doc comment implies `organisation` is
 * populated too). Mirrors `main.tsx`'s dev-bypass-era `hasRealPayRuns`/
 * `hasRealOrganisation` stance: routing a locally-edited draft into a real
 * `entity.update` mutation over an entity this session never actually read
 * would silently corrupt it -- so no save affordance is ever wired before the
 * read has succeeded.
 *
 * Takes the PRIMITIVE `sessionFactsStatus` (not the whole `SessionFactsResult`
 * object) deliberately -- this function only ever reads `.status`. Passing
 * just the field it depends on (rather than the whole object) means a future
 * change that starts reading e.g. `sessionFactsState.organisation` inside
 * here is forced to widen this signature to say so explicitly, instead of
 * silently closing over a field this function's caller (`main.tsx`'s
 * `useMemo`, keyed on this same primitive) never declared as a dependency —
 * exactly the class of stale-read bug `eslint-plugin-react-hooks`'s
 * `exhaustive-deps` would normally catch, which this app's `eslint.config.js`
 * does not enable (adversarial-review finding, C11 #22333, second round).
 *
 * Structurally, `main.tsx`'s `AuthGate` already never renders
 * `<WageProStoreProvider>` until `sessionFactsStatus === 'ready'` (see
 * `resolveAuthGateScreen`), so in practice this function's `sessionFactsStatus`
 * argument is always `'ready'` by the time it is called. It is still written
 * as this explicit, independently unit-tested predicate (not just assumed
 * from that call-site ordering) so a future refactor of the gating order in
 * `AuthGate` can't silently wire a real save mutation over stale or absent
 * read data.
 */
export function resolveOrganisationActions(
  entityContext: OAuthEntityContext,
  sessionFactsStatus: SessionFactsResult['status'],
  /**
   * Called after a save succeeds AND the delta actually included `name` --
   * i.e. the organisation was genuinely renamed. Wired by `main.tsx` to
   * `useSessionFacts()`'s `retry` so the shared `session.company_name`
   * (resolved once at boot from that same `entity.clientGet` response, see
   * `use_session_facts.ts`'s module docblock) refreshes without requiring a
   * full page reload -- previously, a successful rename left the OLD name
   * displayed in the app chrome/nav until reload (adversarial-review
   * finding, C11 #22333, second round). Not called for a save that changed
   * only `financial_year_end`/`currency_code` -- those don't affect
   * `company_name`, so re-fetching session facts for them would be a wasted
   * round trip.
   */
  onOrganisationRenamed: () => void,
  /** Threaded straight through to `saveRealOrganisationSettings` -- test-only seam, defaults to the real bearer client in production. */
  deps?: RealSettingsDeps,
): OrganisationActionsController | undefined {
  if (entityContext.status !== 'ready') return undefined;
  if (sessionFactsStatus !== 'ready') return undefined;
  const { firm_id, entity_id } = entityContext;
  return {
    save: async (input) => {
      const outcome = await saveRealOrganisationSettings(
        input,
        { firm_id, entity_id },
        deps,
      );
      if (outcome.ok && input.name !== undefined) {
        onOrganisationRenamed();
      }
      return outcome;
    },
  };
}
